import Float from './Float'
import cn from 'utils/cn'
import { AnimatePresence, motion, Variants } from 'framer-motion'
import React, { useEffect } from 'react'

export interface DialogProps extends React.HTMLProps<HTMLDivElement> {
  /**
   * Controls whether the dialog is visible.
   */
  show?: boolean
  /**
   * Additional class names for the dialog container.
   */
  className?: string
  /**
   * Custom motion variants for framer-motion animations.
   */
  motionProps?: Variants
  /**
   * Callback fired when the dialog requests to close (e.g. clicking backdrop or pressing Escape).
   */
  onClose?: VoidFunction
  /**
   * Whether to show a backdrop behind the dialog.
   */
  hasBackdrop?: boolean
  /**
   * Additional class names for the backdrop element.
   */
  backdropClassName?: string
}

/**
 * @description Primitive that can be used to build floating widgets that needs to be on top of the entire app, such as modal, notification, etc.
 * Note that this is not fitting for popover or tooltip components.
 */
const Dialog = ({
  className,
  show = false,
  motionProps = {},
  hasBackdrop,
  onClose,
  backdropClassName,
  ...props
}: DialogProps) => {
  const variants = {
    open: {
      opacity: 1,
      y: '0%'
    },
    closed: {
      opacity: 0,
      y: '-100%'
    }
  }

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && show) {
        onClose?.()
      }
    }
    document.addEventListener('keydown', listener)
    return () => document.removeEventListener('keydown', listener)
  }, [show, onClose])

  return (
    <Float>
      <AnimatePresence>
        {show && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={cn(
                'w-screen h-screen fixed top-0 z-modalBackdrop',
                hasBackdrop && 'bg-dark/50',
                backdropClassName
              )}
              onClick={onClose}
            />
            <motion.div
              variants={{ ...variants, ...motionProps }}
              initial="closed"
              animate="open"
              exit="closed"
              className={cn(['fixed z-modal', className])}
            >
              <div {...props} />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </Float>
  )
}

export default Dialog
